import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryParam } from '../shared/hooks/location'
import { useApiFormAuthProvider } from '../api-new/state/auth'
import { useLoad } from '../shared/hooks/load'
import { OpenAccountConsent } from './open-account-consent'
import { OpenLinkConfirm } from './open-link-confirm'
import { REASON, reasonOf } from './open-link-confirm-copy'
import { AuthHead, AuthLoading, AuthMark, AuthPage } from '../shared/sso-workspace/auth-card'
import { ErrorReference } from '../shared/error/error-reference'
import { SignInLinkFailed, SignInLinkSpent, useSignInLinkSpent } from '../shared/sso-workspace/sign-in-link-spent'
import { confirmedNextURL, getLinkConfirmation, isLinkConfirmationRequired } from '../shared/sso-workspace/link-confirm-api'

// /open/link?token=… is two pages in one (SSO link confirmation, owner
// decision Option A, plan 5.1). Both land here:
//
//   - a sign-in auth HOLDS: a public sign-in button (Google, Microsoft,
//     Facebook...) met an existing account it isn't connected to, so the
//     person confirms the account is theirs first (open-link-confirm.js);
//   - an SSO impersonation hand-off (a staff admin signing in as someone
//     through a connection that account has no link to): today's "Link your
//     EducationXR account" consent (open-account-consent.js).
//
// GET auth-provider/:token/link-confirmation tells them apart: required
// true is a hold; required false (or a 404 from an auth that predates the
// route) is the consent; required false with confirmed true -- confirmed
// in another tab, or the account got linked meanwhile -- goes straight on
// to the organization step. The hold is bound to the browser that started
// the sign-in: anywhere else (and for a hold no browser can prove) auth
// answers link_confirmation_other_browser and the page says to start again
// here. A spent token is the calm card.
//
// Every hand-off on is a location.replace, never a push: Back must not
// return to a step whose token moved on.

export const OpenLink = () => {
  const token = useQueryParam('token')

  // loading | consent | confirm | confirmed | other-browser | failed
  const [view, updateView] = useState({ kind: 'loading' })

  const apiFormAuthProviderDetails = useApiFormAuthProvider()

  // A link already used or expired: the calm card (sign-in-link-spent.js).
  const [spent, onSpent] = useSignInLinkSpent()

  const goOn = next => {
    updateView({ kind: 'confirmed' })
    window.location.replace(confirmedNextURL(token, next))
  }

  const check = async () => {
    updateView({ kind: 'loading' })

    let state

    try {
      state = await getLinkConfirmation(token)
    } catch (error) {
      if (onSpent(error)) return
      if (reasonOf(error) === REASON.otherBrowser) {
        updateView({ kind: 'other-browser' })
      } else if (error && error.status === 404) {
        // an auth that predates link confirmation: today's page
        updateView({ kind: 'consent' })
      } else {
        updateView({ kind: 'failed', error })
      }
      return
    }

    if (state && state.required === true) {
      // who (the email) and the way back (the cancel URL); a failure leaves
      // the page without them.
      try {
        await apiFormAuthProviderDetails.get(`${token}/details`)
      } catch (error) {
        if (onSpent(error)) return
      }
      updateView({ kind: 'confirm', state })
      return
    }

    if (state && state.confirmed) {
      goOn(state.next)
      return
    }

    updateView({ kind: 'consent' })
  }

  useLoad(check, [])

  if (spent) {
    return <SignInLinkSpent reason={spent} />
  }

  switch (view.kind) {
    case 'consent':
      return <OpenLinkConsent token={token} onSpent={onSpent} onHeld={check} />

    case 'confirm':
      return (
        <OpenLinkConfirm
          token={token}
          state={view.state}
          details={apiFormAuthProviderDetails.form || {}}
          onConfirmed={goOn}
          onRecheck={check}
          onSpent={onSpent}
          onOtherBrowser={() => updateView({ kind: 'other-browser' })}
        />
      )

    case 'confirmed':
      return (
        <AuthPage labelledBy="link-done-title">
          <AuthHead id="link-done-title" title="Confirmed" mark={<AuthMark tone="ok">✓</AuthMark>} />
          <AuthLoading>Signing you in…</AuthLoading>
        </AuthPage>
      )

    case 'other-browser':
      return (
        <AuthPage labelledBy="link-browser-title">
          {/* A neutral heading (review L4): a hold no browser can prove (made
              at Register or Link, or on a plain-HTTP callback) is answered
              the same, in the very browser the person started in. */}
          <AuthHead id="link-browser-title" title="Start this sign-in again" mark={<AuthMark tone="warn">!</AuthMark>} />
          <p className="m-0">
            This sign-in can only be confirmed in the browser where it started. To sign in here, start again from the sign-in page in this browser. If you were signing in to an EducationXR app, start again from the app.
          </p>
          <Link replace className="btn sw-btn-primary sa-block" to="/auth/email">Back to sign in</Link>
        </AuthPage>
      )

    case 'failed':
      return (
        <AuthPage labelledBy="link-failed-title">
          <AuthHead id="link-failed-title" title="We couldn't load this sign-in" mark={<AuthMark tone="bad">!</AuthMark>} />
          <p className="m-0">Your sign-in worked, but we couldn't check what happens next. Try again, or start again from the sign-in page.</p>
          <ErrorReference error={view.error} />
          <div className="sa-actions">
            <button type="button" className="btn sw-btn-primary sa-block" onClick={check}>Try again</button>
            <Link replace className="btn sw-btn sa-block" to="/auth/email">Back to sign in</Link>
          </div>
        </AuthPage>
      )

    default:
      return (
        <AuthPage labelledBy="link-loading-title">
          <AuthHead id="link-loading-title" title="Signing you in" />
          <AuthLoading>Checking your sign-in…</AuthLoading>
        </AuthPage>
      )
  }
}

// Today's consent (an SSO impersonation hand-off, or an older auth): the
// page itself is open-account-consent.js; this owns its calls:
// GET auth-provider/:token/details for who and which connection,
// GET auth-provider/:token/link on accept, then /open/organization. A link
// refused because the sign-in now waits for confirmation reads the state
// again (onHeld).
const OpenLinkConsent = ({ token, onSpent, onHeld }) => {
  const [error, updateError] = useState()
  // The consent's first call failed otherwise: the "didn't work" card (owner 2026-10-17).
  const [failed, updateFailed] = useState(null)

  const apiFormAuthProvider = useApiFormAuthProvider()
  const apiFormAuthProviderDetails = useApiFormAuthProvider()

  useLoad(async () => {
    try {
      await apiFormAuthProviderDetails.get(`${token}/details`)
    } catch (error) {
      if (!onSpent(error)) updateFailed(error || {})
    }
  }, [])

  const handleSubmit = async () => {
    try {
      await apiFormAuthProvider.get(`${token}/link`)

      let url = new URL(`${process.env.REACT_APP_SITE}/open/organization`)
      url.searchParams.set('token', token)
      // replace: Back must not return to this step's spent token.
      window.location.replace(url.href)
    } catch (error) {
      if (onSpent(error)) return
      if (isLinkConfirmationRequired(error)) {
        onHeld()
        return
      }
      updateError(error && error.data)
    }
  }

  // The details response carries redirectCancelURL on its form; the old page
  // read it off the hook itself, where it is never set.
  const handleCancel = async () =>
    (window.location = apiFormAuthProviderDetails.form.redirectCancelURL || apiFormAuthProviderDetails.redirectCancelURL)

  if (failed) {
    return <SignInLinkFailed error={failed} />
  }

  return <OpenAccountConsent mode="link" details={apiFormAuthProviderDetails} error={error} onSubmit={handleSubmit} onCancel={handleCancel} />
}
